// Help: index (grouped, filtered to your role), search, one article at a time, and "What's new".
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GROUPS, HELP_UPDATED, TOPICS, WHATS_NEW, type HelpTopic } from '../help/content.tsx';
import { useAuth } from '../lib/auth.tsx';

const us = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return `${m}/${d}/${y}`; };

export function Help() {
  const { id } = useParams();
  const { me } = useAuth();
  const [q, setQ] = useState('');
  const mine = useMemo(() => TOPICS.filter((t) => !me || t.roles.includes(me.role)), [me]);
  const found = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return mine;
    return mine.filter((t) => { const hay = `${t.title} ${t.summary} ${t.keywords}`.toLowerCase(); return words.every((w) => hay.includes(w)); });
  }, [q, mine]);
  const topic: HelpTopic | undefined = id && id !== 'whats-new' ? mine.find((t) => t.id === id) : undefined;
  const idx = topic ? mine.indexOf(topic) : -1;

  return (
    <div className="help">
      <aside className="help-index">
        <input type="search" placeholder="Search help…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search help" />
        {GROUPS.map((g) => {
          const items = found.filter((t) => t.group === g);
          if (!items.length) return null;
          return (
            <nav key={g} aria-label={g}>
              <h4>{g}</h4>
              {items.map((t) => <Link key={t.id} to={`/help/${t.id}`} className={t.id === id ? 'on' : ''}>{t.title}</Link>)}
            </nav>
          );
        })}
        {!found.length && <p className="muted small">Nothing matches "{q}".</p>}
        <nav aria-label="What's new"><h4>Updates</h4><Link to="/help/whats-new" className={id === 'whats-new' ? 'on' : ''}>What's new</Link></nav>
        <p className="muted small">Help last updated {us(HELP_UPDATED)}</p>
      </aside>

      <article className="help-body card">
        {id === 'whats-new' ? (
          <>
            <h1>What's new</h1>
            {WHATS_NEW.map((w) => (
              <section key={w.date}><h3>{us(w.date)}</h3><ul>{w.items.map((i) => <li key={i}>{i}</li>)}</ul></section>
            ))}
          </>
        ) : topic ? (
          <>
            <div className="muted small">{topic.group}</div>
            <h1>{topic.title}</h1>
            {topic.body}
            <div className="help-prevnext">
              {idx > 0 ? <Link to={`/help/${mine[idx - 1]!.id}`}>← {mine[idx - 1]!.title}</Link> : <span />}
              {idx < mine.length - 1 ? <Link to={`/help/${mine[idx + 1]!.id}`}>{mine[idx + 1]!.title} →</Link> : <span />}
            </div>
          </>
        ) : id ? (
          <p>That help page was not found. <Link to="/help">Back to the index</Link></p>
        ) : (
          <>
            <h1>Help</h1>
            <p>Everything about using NGF Assessments. Pick a topic on the left, or search. New here? Start with <Link to="/help/welcome">Welcome</Link> and
              {' '}<Link to="/help/sign-in">Signing in</Link>.</p>
            {GROUPS.map((g) => {
              const items = mine.filter((t) => t.group === g);
              if (!items.length) return null;
              return (
                <section key={g} className="help-toc">
                  <h2>{g}</h2>
                  <ul>{items.map((t) => <li key={t.id}><Link to={`/help/${t.id}`}>{t.title}</Link> <span className="muted">— {t.summary}</span></li>)}</ul>
                </section>
              );
            })}
            <p><Link to="/help/whats-new">What's new</Link> · last updated {us(HELP_UPDATED)}</p>
          </>
        )}
      </article>
    </div>
  );
}
